import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import * as microsoftTeams from '@microsoft/teams-js';
import { GetWatercoolerPrompt } from 'redux/actions/watercooler';
import { useAppContext } from 'context';
import Form from './Form';

const Watercooler = (props) => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { prompt, GetWatercoolerPrompt: getRandomPrompt, loading } = props;
  const { 
    tenantId, 
    ts, 
    startedVia, 
    setTs, 
    setStartedVia, 
    setCurrentUserName,
    setChannelName,
    setTeamName,
    setChannelId,
    setTeamId
  } = useAppContext();
  const [disableButton, setDisableButton] = useState(false);

  // Updating the context param variables when the game is opened directly
  useEffect(() => {
    setTs((prev) => prev || params.get('ts') || null);
    setStartedVia((prev) => prev || params.get('startedVia') || null);
    setCurrentUserName((prev) => prev || params.get('userName') || null);
    
    // Set channel context from URL parameters (from GameLauncher)
    const channelId = params.get('channelId');
    const channelName = params.get('channelName');
    const teamId = params.get('teamId');
    const teamName = params.get('teamName');
    
    if (channelId) setChannelId(channelId);
    if (channelName) setChannelName(decodeURIComponent(channelName));
    if (teamId) setTeamId(teamId);
    if (teamName) setTeamName(decodeURIComponent(teamName));
  }, [params, setTs, setStartedVia, setCurrentUserName, setChannelName, setTeamName, setChannelId, setTeamId]);

  const fetchWatercoolerPrompt = (tid = null) => {
    getRandomPrompt(tenantId || tid);
  };

  const handleSubmit = () => {
    setDisableButton(true);
    const formData = {};
    if (prompt) {
      formData.prompt = prompt;
    }
    if (ts && ts !== 'null') {
      formData.ts = ts;
    }
    if (startedVia) {
      formData.startedVia = startedVia;
    }
    
    // Get conversation context ONLY if launched from GameLauncher
    const conversationId = params.get('channelId');
    const launchedFromHome = params.get('startedVia') === 'home';
    
    if (conversationId && launchedFromHome) {
      // Approach 2: Match messaging extension payload structure exactly
      formData.launchedFromHomeTab = true;
      const fullPayload = {
        name: "composeExtension/submitAction",  // Match working payload
        conversation: {                         // At root level like messaging extensions
          id: conversationId
        },
        value: "watercooler_confirm",
        formData: formData
      };
      
      
      // Try submitting the structured payload
      microsoftTeams.dialog.url.submit(
        fullPayload,
        process.env.REACT_APP_TEAMS_APP_ID
      );
    } else {
      // Original flow for existing messaging extensions
      const payload = { value: 'watercooler_confirm', formData };
      microsoftTeams.dialog.url.submit(
        payload,
        process.env.REACT_APP_TEAMS_APP_ID
      );
    }
  };

  useEffect(() => {
    microsoftTeams.app.initialize();
    microsoftTeams.app.getContext().then((context) => {
      fetchWatercoolerPrompt(context.user.tenant.id);
    });
    microsoftTeams.app.notifySuccess();
  }, []);

  return (
    <Form
      prompt={prompt.text}
      gif={prompt.imageUrl}
      loading={loading}
      disable={disableButton || loading}
      handleSubmit={handleSubmit}
      handleShuffle={fetchWatercoolerPrompt}
    />
  );
};

Watercooler.propTypes = {
  prompt: PropTypes.shape({
    text: PropTypes.string,
    _id: PropTypes.string,
    imageUrl: PropTypes.string
  }).isRequired,
  GetWatercoolerPrompt: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

const mapStateToProps = ({ watercooler: { prompt, loading } }) => ({ prompt, loading });

export default connect(mapStateToProps, {
  GetWatercoolerPrompt
})(Watercooler);
